import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  BookOpen, Plus, Edit2, Trash2, Save, X, Loader2,
  MapPin, Calendar, Smile, Meh, Frown, Heart, Star, Sun, Share2, Copy, Check, Link2
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LABELS = {
  it: { title: 'Diario di Viaggio', subtitle: 'Registra i tuoi ricordi di viaggio', newEntry: 'Nuova Pagina', titleField: 'Titolo', destination: 'Destinazione', content: 'Racconta la tua esperienza...', mood: 'Umore', save: 'Salva', cancel: 'Annulla', delete: 'Elimina', edit: 'Modifica', noEntries: 'Il tuo diario è vuoto', noEntriesDesc: 'Inizia a scrivere i tuoi ricordi di viaggio!', confirmDelete: 'Eliminare questa pagina?', photoUrl: 'URL foto (opzionale)', shareDiary: 'Condividi Diario', shareLink: 'Link copiato!', generating: 'Generando link...' },
  en: { title: 'Travel Diary', subtitle: 'Record your travel memories', newEntry: 'New Entry', titleField: 'Title', destination: 'Destination', content: 'Share your experience...', mood: 'Mood', save: 'Save', cancel: 'Cancel', delete: 'Delete', edit: 'Edit', noEntries: 'Your diary is empty', noEntriesDesc: 'Start writing your travel memories!', confirmDelete: 'Delete this entry?', photoUrl: 'Photo URL (optional)', shareDiary: 'Share Diary', shareLink: 'Link copied!', generating: 'Generating link...' },
  pl: { title: 'Dziennik Podróży', subtitle: 'Zapisz wspomnienia z podróży', newEntry: 'Nowy wpis', titleField: 'Tytuł', destination: 'Cel podróży', content: 'Opisz swoje doświadczenie...', mood: 'Nastrój', save: 'Zapisz', cancel: 'Anuluj', delete: 'Usuń', edit: 'Edytuj', noEntries: 'Twój dziennik jest pusty', noEntriesDesc: 'Zacznij zapisywać wspomnienia!', confirmDelete: 'Usunąć ten wpis?', photoUrl: 'URL zdjęcia (opcjonalnie)', shareDiary: 'Udostępnij', shareLink: 'Link skopiowany!', generating: 'Generowanie...' },
  fr: { title: 'Journal de Voyage', subtitle: 'Enregistrez vos souvenirs', newEntry: 'Nouvelle entrée', titleField: 'Titre', destination: 'Destination', content: 'Partagez votre expérience...', mood: 'Humeur', save: 'Enregistrer', cancel: 'Annuler', delete: 'Supprimer', edit: 'Modifier', noEntries: 'Votre journal est vide', noEntriesDesc: 'Commencez à écrire vos souvenirs !', confirmDelete: 'Supprimer cette entrée ?', photoUrl: 'URL photo (optionnel)', shareDiary: 'Partager', shareLink: 'Lien copié !', generating: 'Génération...' },
  es: { title: 'Diario de Viaje', subtitle: 'Registra tus recuerdos', newEntry: 'Nueva entrada', titleField: 'Título', destination: 'Destino', content: 'Comparte tu experiencia...', mood: 'Ánimo', save: 'Guardar', cancel: 'Cancelar', delete: 'Eliminar', edit: 'Editar', noEntries: 'Tu diario está vacío', noEntriesDesc: '¡Empieza a escribir tus recuerdos!', confirmDelete: '¿Eliminar esta entrada?', photoUrl: 'URL de foto (opcional)', shareDiary: 'Compartir', shareLink: '¡Link copiado!', generating: 'Generando...' },
  de: { title: 'Reisetagebuch', subtitle: 'Halte deine Reiseerinnerungen fest', newEntry: 'Neuer Eintrag', titleField: 'Titel', destination: 'Reiseziel', content: 'Teile deine Erfahrung...', mood: 'Stimmung', save: 'Speichern', cancel: 'Abbrechen', delete: 'Löschen', edit: 'Bearbeiten', noEntries: 'Dein Tagebuch ist leer', noEntriesDesc: 'Beginne mit deinen Erinnerungen!', confirmDelete: 'Eintrag löschen?', photoUrl: 'Foto-URL (optional)', shareDiary: 'Teilen', shareLink: 'Link kopiert!', generating: 'Generiere...' },
  ru: { title: 'Дневник путешествий', subtitle: 'Записывайте воспоминания', newEntry: 'Новая запись', titleField: 'Заголовок', destination: 'Направление', content: 'Поделитесь впечатлениями...', mood: 'Настроение', save: 'Сохранить', cancel: 'Отмена', delete: 'Удалить', edit: 'Редактировать', noEntries: 'Ваш дневник пуст', noEntriesDesc: 'Начните записывать воспоминания!', confirmDelete: 'Удалить эту запись?', photoUrl: 'URL фото (необязательно)', shareDiary: 'Поделиться', shareLink: 'Ссылка скопирована!', generating: 'Генерация...' },
};

const MOODS = [
  { key: 'happy', icon: Smile, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { key: 'love', icon: Heart, color: 'text-rose-400', bg: 'bg-rose-500/10' },
  { key: 'excited', icon: Star, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  { key: 'relaxed', icon: Sun, color: 'text-sky-400', bg: 'bg-sky-500/10' },
  { key: 'neutral', icon: Meh, color: 'text-slate-400', bg: 'bg-slate-500/10' },
  { key: 'tired', icon: Frown, color: 'text-orange-400', bg: 'bg-orange-500/10' },
];

function DiaryForm({ entry, onSave, onCancel, labels }) {
  const [form, setForm] = useState({
    title: entry?.title || '',
    content: entry?.content || '',
    destination: entry?.destination || '',
    mood: entry?.mood || 'happy',
    photo_url: entry?.photo_url || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <motion.form onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4"
      data-testid="diary-form">
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-slate-400 mb-1.5">{labels.titleField}</label>
          <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            required className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-teal-500/40 transition-colors"
            placeholder={labels.titleField} data-testid="diary-title-input" />
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1.5">{labels.destination}</label>
          <input type="text" value={form.destination} onChange={e => setForm(f => ({ ...f, destination: e.target.value }))}
            required className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-teal-500/40 transition-colors"
            placeholder={labels.destination} data-testid="diary-destination-input" />
        </div>
      </div>

      <div>
        <label className="block text-xs text-slate-400 mb-1.5">{labels.content}</label>
        <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
          required rows={5}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-teal-500/40 transition-colors resize-none"
          placeholder={labels.content} data-testid="diary-content-input" />
      </div>

      <div>
        <label className="block text-xs text-slate-400 mb-1.5">{labels.photoUrl}</label>
        <input type="url" value={form.photo_url} onChange={e => setForm(f => ({ ...f, photo_url: e.target.value }))}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-teal-500/40 transition-colors"
          placeholder="https://..." data-testid="diary-photo-input" />
      </div>

      {/* Mood selector */}
      <div>
        <label className="block text-xs text-slate-400 mb-2">{labels.mood}</label>
        <div className="flex gap-2 flex-wrap">
          {MOODS.map(m => (
            <button key={m.key} type="button" onClick={() => setForm(f => ({ ...f, mood: m.key }))}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-all ${
                form.mood === m.key ? `${m.bg} ${m.color} border-current` : 'bg-white/5 text-slate-400 border-white/10 hover:bg-white/10'
              }`} data-testid={`mood-${m.key}`}>
              <m.icon className="w-3.5 h-3.5" /> {m.key}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={saving}
          className="bg-teal-500 hover:bg-teal-400 disabled:bg-slate-700 text-black font-semibold px-6 py-2.5 rounded-xl text-sm transition-all flex items-center gap-2"
          data-testid="diary-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {labels.save}
        </button>
        <button type="button" onClick={onCancel}
          className="text-slate-400 hover:text-white px-4 py-2.5 rounded-xl text-sm hover:bg-white/5 transition-all"
          data-testid="diary-cancel-btn">
          {labels.cancel}
        </button>
      </div>
    </motion.form>
  );
}

function DiaryCard({ entry, onEdit, onDelete, labels }) {
  const mood = MOODS.find(m => m.key === entry.mood) || MOODS[0];
  const MoodIcon = mood.icon;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-white/20 transition-all"
      data-testid={`diary-entry-${entry.entry_id}`}>
      
      {entry.photo_url && (
        <div className="h-40 overflow-hidden">
          <img src={entry.photo_url} alt={entry.title} className="w-full h-full object-cover" onError={(e) => e.target.style.display = 'none'} />
        </div>
      )}
      
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-white font-semibold">{entry.title}</h3>
            <div className="flex items-center gap-3 mt-1">
              <span className="flex items-center gap-1 text-xs text-teal-400">
                <MapPin className="w-3 h-3" /> {entry.destination}
              </span>
              <span className="flex items-center gap-1 text-xs text-slate-500">
                <Calendar className="w-3 h-3" /> {new Date(entry.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
          <div className={`w-8 h-8 rounded-full ${mood.bg} flex items-center justify-center`}>
            <MoodIcon className={`w-4 h-4 ${mood.color}`} />
          </div>
        </div>

        <p className="text-slate-300 text-sm leading-relaxed line-clamp-4">{entry.content}</p>

        <div className="flex gap-2 mt-4 pt-3 border-t border-white/[0.06]">
          <button onClick={() => onEdit(entry)}
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-teal-400 transition-colors px-2 py-1 rounded-lg hover:bg-white/5"
            data-testid={`diary-edit-${entry.entry_id}`}>
            <Edit2 className="w-3 h-3" /> {labels.edit}
          </button>
          <button onClick={() => onDelete(entry.entry_id)}
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-rose-400 transition-colors px-2 py-1 rounded-lg hover:bg-rose-500/5"
            data-testid={`diary-delete-${entry.entry_id}`}>
            <Trash2 className="w-3 h-3" /> {labels.delete}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function TravelDiary() {
  const { i18n } = useTranslation();
  const lang = LABELS[i18n.language] ? i18n.language : 'en';
  const L = LABELS[lang];

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [shareLink, setShareLink] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);

  const token = localStorage.getItem('session_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/diary/entries`, { headers });
      setEntries(res.data.entries || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleShare = async () => {
    setSharing(true);
    try {
      const res = await axios.post(`${API}/diary/share`, { entry_ids: [] }, { headers });
      const url = `${window.location.origin}?page=diary&id=${res.data.share_id}`;
      setShareLink(url);
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch { /* ignore */ }
    setSharing(false);
  };

  const handleSave = async (form) => {
    try {
      if (editEntry) {
        await axios.put(`${API}/diary/entries/${editEntry.entry_id}`, form, { headers });
      } else {
        await axios.post(`${API}/diary/entries`, form, { headers });
      }
      setShowForm(false);
      setEditEntry(null);
      fetchEntries();
    } catch { /* ignore */ }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm(L.confirmDelete)) return;
    try {
      await axios.delete(`${API}/diary/entries/${entryId}`, { headers });
      setEntries(prev => prev.filter(e => e.entry_id !== entryId));
    } catch { /* ignore */ }
  };

  const handleEdit = (entry) => {
    setEditEntry(entry);
    setShowForm(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-teal-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="travel-diary">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-teal-400" /> {L.title}
          </h2>
          <p className="text-slate-400 text-sm mt-0.5">{L.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {entries.length > 0 && (
            <button onClick={handleShare} disabled={sharing}
              className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white font-medium px-4 py-2 rounded-xl text-sm transition-all disabled:opacity-50"
              data-testid="diary-share-btn">
              {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Share2 className="w-4 h-4" />}
              {copied ? L.shareLink : L.shareDiary}
            </button>
          )}
          {!showForm && (
            <button onClick={() => { setEditEntry(null); setShowForm(true); }}
              className="flex items-center gap-1.5 bg-teal-500 hover:bg-teal-400 text-black font-semibold px-4 py-2 rounded-xl text-sm transition-all"
              data-testid="diary-new-btn">
              <Plus className="w-4 h-4" /> {L.newEntry}
            </button>
          )}
        </div>
      </div>

      {/* Share link banner */}
      <AnimatePresence>
        {shareLink && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="bg-teal-500/10 border border-teal-500/20 rounded-xl p-3 flex items-center gap-3 overflow-hidden">
            <Link2 className="w-4 h-4 text-teal-400 flex-shrink-0" />
            <input type="text" value={shareLink} readOnly className="flex-1 bg-transparent text-teal-300 text-xs font-mono outline-none" data-testid="diary-share-url" />
            <button onClick={() => { navigator.clipboard.writeText(shareLink); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
              className="text-teal-400 hover:text-teal-300 p-1" data-testid="diary-copy-link">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
            <button onClick={() => setShareLink(null)} className="text-slate-500 hover:text-white p-1">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Form */}
      <AnimatePresence>
        {showForm && (
          <DiaryForm
            entry={editEntry}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditEntry(null); }}
            labels={L}
          />
        )}
      </AnimatePresence>

      {/* Entries */}
      {entries.length === 0 && !showForm ? (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-12 text-center">
          <BookOpen className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-white font-semibold mb-2">{L.noEntries}</h3>
          <p className="text-slate-400 text-sm">{L.noEntriesDesc}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {entries.map(entry => (
            <DiaryCard key={entry.entry_id} entry={entry} onEdit={handleEdit} onDelete={handleDelete} labels={L} />
          ))}
        </div>
      )}
    </div>
  );
}
